import { createContext, useContext } from "react";
import type { DeviceRecord, OutboxEntry, VavrDb } from "@vavr/client";
import type { AppPlatform } from "../shared/platform.ts";
import type { TeacherAction } from "./events.ts";

/** `org_kind` "operator": a bus company's depot (no families there). */
export interface StaffSite { id: string; name: string; tz: string; org_id: string; org_kind?: string }

export interface TeacherCtx {
  platform: AppPlatform;
  me: { id: string; name: string; email: string };
  site: StaffSite;
  /** Only a bus driver here (no core staff role): the app is about buses, not classes. */
  busOnly?: boolean;
  /** Other sites this person works at, for the site switcher. */
  sites: StaffSite[];
  switchSite: (id: string) => void;
  db: VavrDb;
  device: DeviceRecord | null;
  /** Unsent outbox entries (signed on this phone, waiting to reach the site). */
  pending: OutboxEntry[];
  /** Signs an action with this phone's key, queues it, and tries to send it now. */
  record: (a: TeacherAction) => Promise<void>;
  /** Bumped whenever the board may have changed (socket message, local action). */
  version: number;
  refresh: () => void;
  live: boolean;
  signOut: () => Promise<void>;
  setupDevice: () => Promise<void>;
}

export const TeacherContext = createContext<TeacherCtx | null>(null);

export function useTeacher(): TeacherCtx {
  const c = useContext(TeacherContext);
  if (!c) throw new Error("useTeacher outside TeacherRoot");
  return c;
}

export const T = "/teacher";

/** Roles that may use the teacher app at a site (they hold handover.capture). */
export const STAFF_ROLES = new Set(["teacher", "door_staff", "site_admin", "org_admin"]);
